import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `generate:` scope (F1/F-Estep/F15/F48),
 * following the exact `web:` precedent (`apps/web/src/i18n/webCatalog.ts` +
 * `useWebT.ts`) — NOT a second i18n runtime (E18/§13.3 ban), just physically
 * scoped here because `packages/i18n/**` is outside this task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/generate.json`.
 */
const en = {
  'wizard.title': 'Generate a document',
  'wizard.newDocument': 'New document',
  'wizard.step': 'Step {step} of {total}',
  'wizard.stepFraction': '{step} / {total}',
  'wizard.progressLabel': 'Setup progress',
  'wizard.back': 'Back',
  'wizard.next': 'Next',
  'step.template.title': 'What are you making?',
  'step.topic.title': 'What’s it about?',
  'step.topic.titleLabel': 'Document title',
  'step.topic.briefLabel': 'Brief (optional)',
  'step.topic.briefHint': 'A sentence or two about what you want — this feeds the outline.',
  'step.sources.title': 'Add sources',
  'step.sources.unavailable':
    'Uploading your own sources and picking public-domain books aren’t available in this build yet — you can skip this step.',
  'step.length.title': 'Length & difficulty',
  'step.length.chapterCountLabel': 'Target chapter count (optional)',
  'step.length.difficultyLabel': 'Difficulty',
  'step.length.languageLabel': 'Language',
  'step.length.illustrateLabel': 'Add illustrations',
  'step.length.illustrateHint': 'PRO feature — the setting is saved either way; illustration only runs for PRO accounts.',
  'step.persona.title': 'Pick a voice',
  'step.persona.unavailable':
    'The persona gallery isn’t available in this build yet — you can skip this step and generate with the default voice.',
  'step.persona.skip': 'Skip — default voice',
  'step.review.title': 'Review & generate',
  'step.review.summaryTitle': 'Summary',
  'step.review.field.title': 'Title',
  'step.review.field.kind': 'Type',
  'step.review.field.difficulty': 'Difficulty',
  'step.review.field.language': 'Language',
  'step.review.field.chapters': 'Target chapters',
  'step.review.costLabel': 'Estimated cost',
  'unit.credits': '{count} cr',
  'step.review.costLoading': 'Estimating cost…',
  'step.review.costError': 'Couldn’t estimate the cost — you can still try to generate.',
  'step.review.balanceLine': '{balance} → {balanceAfter} credits',
  'step.review.submit': 'Confirm & generate',
  'step.review.confirmAction': 'Generate',
  'confirm.title': 'Generate',
  'error.insufficientCredits.title': 'Not enough credits',
  'error.insufficientCredits.description': 'You don’t have enough credits for this generation yet.',
  'error.insufficientCredits.cta': 'Buy more credits',
  'error.contentPolicy.title': 'This brief conflicts with our content policy',
  'error.contentPolicy.notCharged': 'Not charged · 0 cr',
  'error.contentPolicy.cta': 'Edit & retry',
  'error.generic': 'Couldn’t start generation. Please try again.',
  'kind.book': 'Book',
  'kind.article': 'Article',
  'kind.report': 'Report',
  'difficulty.basic': 'Basic',
  'difficulty.intermediate': 'Intermediate',
  'difficulty.advanced': 'Advanced',
  'difficulty.mastery': 'Mastery',
  'policyCategory.hate': 'Hate',
  'policyCategory.harassment': 'Harassment',
  'policyCategory.sexual': 'Sexual content',
  'policyCategory.sexual_minors': 'Sexual content involving minors',
  'policyCategory.violence': 'Violence',
  'policyCategory.self_harm': 'Self-harm',
  'policyCategory.illegal': 'Illegal activity',
  'policyCategory.malware': 'Malware',
  'policyCategory.pii': 'Personal information',
  'error.contentPolicy.readPolicy': 'Read our content policy',
  'toast.generateStarted.action': 'Started generating',
} as const;

const ro: Record<keyof typeof en, string> = {
  'wizard.title': 'Generează un document',
  'wizard.newDocument': 'Document nou',
  'wizard.step': 'Pasul {step} din {total}',
  'wizard.stepFraction': '{step} / {total}',
  'wizard.progressLabel': 'Progresul configurării',
  'wizard.back': 'Înapoi',
  'wizard.next': 'Următorul',
  'step.template.title': 'Ce creezi?',
  'step.topic.title': 'Despre ce este vorba?',
  'step.topic.titleLabel': 'Titlul documentului',
  'step.topic.briefLabel': 'Descriere (opțional)',
  'step.topic.briefHint': 'Câteva propoziții despre ce îți dorești — acestea alimentează structura.',
  'step.sources.title': 'Adaugă surse',
  'step.sources.unavailable':
    'Încărcarea propriilor surse și alegerea cărților de domeniu public nu sunt disponibile încă în această versiune — poți sări peste acest pas.',
  'step.length.title': 'Lungime și dificultate',
  'step.length.chapterCountLabel': 'Număr țintă de capitole (opțional)',
  'step.length.difficultyLabel': 'Dificultate',
  'step.length.languageLabel': 'Limbă',
  'step.length.illustrateLabel': 'Adaugă ilustrații',
  'step.length.illustrateHint': 'Funcție PRO — setarea se salvează oricum; ilustrarea rulează doar pentru conturile PRO.',
  'step.persona.title': 'Alege o voce',
  'step.persona.unavailable':
    'Galeria de persona nu este disponibilă încă în această versiune — poți sări peste acest pas și genera cu vocea implicită.',
  'step.persona.skip': 'Sări peste — vocea implicită',
  'step.review.title': 'Revizuiește și generează',
  'step.review.summaryTitle': 'Rezumat',
  'step.review.field.title': 'Titlu',
  'step.review.field.kind': 'Tip',
  'step.review.field.difficulty': 'Dificultate',
  'step.review.field.language': 'Limbă',
  'step.review.field.chapters': 'Capitole țintă',
  'step.review.costLabel': 'Cost estimat',
  'unit.credits': '{count, plural, one {# credit} few {# credite} other {# de credite}}',
  'step.review.costLoading': 'Se estimează costul…',
  'step.review.costError': 'Nu am putut estima costul — poți încerca oricum să generezi.',
  'step.review.balanceLine': '{balance} → {balanceAfter} credite',
  'step.review.submit': 'Confirmă și generează',
  'step.review.confirmAction': 'Generează',
  'confirm.title': 'Generează',
  'error.insufficientCredits.title': 'Credite insuficiente',
  'error.insufficientCredits.description': 'Nu ai încă suficiente credite pentru această generare.',
  'error.insufficientCredits.cta': 'Cumpără mai multe credite',
  'error.contentPolicy.title': 'Această descriere încalcă politica noastră de conținut',
  'error.contentPolicy.notCharged': 'Netaxat · 0 cr',
  'error.contentPolicy.cta': 'Editează și reîncearcă',
  'error.generic': 'Nu am putut porni generarea. Te rugăm să încerci din nou.',
  'kind.book': 'Carte',
  'kind.article': 'Articol',
  'kind.report': 'Raport',
  'difficulty.basic': 'De bază',
  'difficulty.intermediate': 'Intermediar',
  'difficulty.advanced': 'Avansat',
  'difficulty.mastery': 'Măiestrie',
  'policyCategory.hate': 'Ură',
  'policyCategory.harassment': 'Hărțuire',
  'policyCategory.sexual': 'Conținut sexual',
  'policyCategory.sexual_minors': 'Conținut sexual cu minori',
  'policyCategory.violence': 'Violență',
  'policyCategory.self_harm': 'Automutilare',
  'policyCategory.illegal': 'Activitate ilegală',
  'policyCategory.malware': 'Malware',
  'policyCategory.pii': 'Date cu caracter personal',
  'error.contentPolicy.readPolicy': 'Citește politica noastră de conținut',
  'toast.generateStarted.action': 'A început generarea',
};

export const GENERATE_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type GenerateCatalogKey = keyof typeof en;
